import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { isSameMonth } from "date-fns";
import { useCallback, useMemo, useState } from "react";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { RatingBadge } from "@/design-system/RatingBadge";
import { createDashboardColumnHelper, TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { useBool } from "@/hooks/useBool";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useAnalyticsScope } from "@/modules/analytics/AnalyticsScopeContext";
import { ANALYTICS_TABLE_PAGE_SIZE } from "@/modules/analytics/constants";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import type {
  TicketAnalyticsCategory,
  TicketAnalyticsDateRange,
  TicketAnalyticsDayBucket,
  TicketAnalyticsRow,
} from "@/queries/analytics/ticket-reporting-types";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import { isValueInDistributionBucket } from "./chart-filters";
import { TicketAnalyticsContent } from "./TicketAnalyticsContent";
import { TicketAnalyticsFilterDrawer, type TicketAnalyticsFilterValues } from "./TicketAnalyticsFilterDrawer";

const CATEGORY_TABLE_SIZE = 8;

export interface TicketAnalyticsDateRangeOption {
  value: TicketAnalyticsDateRange;
  name: string;
}

export type TicketAnalyticsChartFilter =
  | {
      type: "month";
      value: string;
      label: string;
    }
  | {
      type: "backlogAge" | "resolution";
      value: TicketAnalyticsDayBucket;
      label: string;
    }
  | undefined;

/**
 * Shows the story-led ticket view in `focused` mode and every available ticket insight in `all` mode.
 * Defaults to `focused` when no mode is provided.
 */
export function TicketAnalyticsPage({ mode = "focused" }: { mode?: "focused" | "all" }): React.ReactNode {
  const { i18n, t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const analyticsScope = useAnalyticsScope();
  const [selectedDateRange, setSelectedDateRange] = useState<TicketAnalyticsDateRange>("last6Months");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [reportingNow] = useState(Date.now);
  const [chartFilter, setChartFilter] = useState<TicketAnalyticsChartFilter>();
  const [filter, setFilter] = useState<TicketAnalyticsFilterValues>({
    categoryId: "all",
    status: "all",
    visibility: "all",
  });
  const [isFilterOpened, filterOpenHandlers] = useBool();

  const dateRangeOptions = useMemo<NonEmptyArray<TicketAnalyticsDateRangeOption>>(
    () => [
      { value: "last6Months", name: t("common.date-range.last-months", { count: 6 }) },
      { value: "lastYear", name: t("common.date-range.last-year") },
    ],
    [t],
  );
  const selectedDateRangeOption = dateRangeOptions.find((option) => option.value === selectedDateRange)!;
  const query = {
    dateRange: selectedDateRange,
    categoryId: filter.categoryId === "all" ? undefined : filter.categoryId,
    status: filter.status === "all" ? undefined : filter.status,
    visibility: filter.visibility === "all" ? undefined : filter.visibility,
  };
  const projectResult = useQuery({
    ...analyticsQueries.ticketReporting(query),
    placeholderData: keepPreviousData,
    enabled: analyticsScope.type === "project",
  });
  const portfolioResult = useQuery({
    ...analyticsQueries.portfolioTicketReporting({
      ...query,
      projectIds: analyticsScope.type === "portfolio" ? analyticsScope.projectIds : [],
    }),
    placeholderData: keepPreviousData,
    enabled: analyticsScope.type === "portfolio" && !analyticsScope.isLoading,
  });
  const { data, isLoading, isFetching, error } = analyticsScope.type === "portfolio" ? portfolioResult : projectResult;

  const filteredTickets = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();

    return (data?.tickets ?? []).filter((ticket) => {
      const matchesSearch =
        !normalizedSearch ||
        ticket.title.toLocaleLowerCase().includes(normalizedSearch) ||
        ticket.reporterName.toLocaleLowerCase().includes(normalizedSearch);

      if (!matchesSearch || !chartFilter) {
        return matchesSearch;
      }

      if (chartFilter.type === "month") {
        return isSameMonth(new Date(ticket.createdAt), new Date(chartFilter.value));
      }
      if (chartFilter.type === "backlogAge") {
        return (
          ticket.status.type !== "closed" &&
          isValueInDistributionBucket(
            Math.max(0, (reportingNow - new Date(ticket.createdAt).getTime()) / (1000 * 60 * 60 * 24)),
            chartFilter.value,
          )
        );
      }

      return (
        ticket.resolutionDays !== undefined && isValueInDistributionBucket(ticket.resolutionDays, chartFilter.value)
      );
    });
  }, [chartFilter, data?.tickets, reportingNow, search]);
  const pageCount = Math.max(1, Math.ceil(filteredTickets.length / ANALYTICS_TABLE_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const paginatedTickets = filteredTickets.slice(
    currentPage * ANALYTICS_TABLE_PAGE_SIZE,
    (currentPage + 1) * ANALYTICS_TABLE_PAGE_SIZE,
  );
  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<TicketAnalyticsRow>();

    return [
      helper.accessor("title", {
        header: t("page.analytics-tickets.table.ticket"),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("category.name", {
        header: t("page.analytics-tickets.table.category"),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("status", {
        header: t("page.analytics-tickets.table.status"),
        cell: (cell) => (
          <TicketStatus labelColor={cell.getValue().color} description={cell.getValue().name} variant="filled" />
        ),
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
      }),
      helper.accessor("visibility", {
        header: t("page.analytics-tickets.table.visibility"),
        cell: (cell) => (
          <Badge variant={cell.getValue() === "collective" ? "special" : "default"}>
            {t(`page.analytics-tickets.filters.visibility.${cell.getValue()}` as const)}
          </Badge>
        ),
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
      }),
      helper.accessor("createdAt", {
        header: t("page.analytics-tickets.table.created"),
        cell: (cell) => <span>{formatDate(i18n, "datetimeShort", cell.getValue())}</span>,
        minSize: TABLE_COLUMN_SIZES.DATE_TIME.MIN,
        size: TABLE_COLUMN_SIZES.DATE_TIME.DEFAULT,
      }),
      helper.accessor("resolutionDays", {
        header: t("page.analytics-tickets.table.resolution-time"),
        cell: (cell) => {
          const resolutionDays = cell.getValue();

          return (
            <span>
              {resolutionDays === undefined ? "-" : t("page.analytics-tickets.value.days", { count: resolutionDays })}
            </span>
          );
        },
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("rating", {
        header: t("page.analytics-tickets.table.rating"),
        cell: (cell) => {
          const rating = cell.getValue();

          return rating === undefined ? "-" : <RatingBadge rating={rating} outOf={10} />;
        },
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
    ];
  }, [i18n, t]);
  const ticketTable = useResizableTable(TABLE_KEYS.ANALYTICS_TICKETS, {
    columns,
    data: paginatedTickets,
    initialState: {
      sorting: [{ id: "createdAt", desc: true }],
    },
  });
  const handleCategoryFilter = useCallback((categoryId: string) => {
    setFilter((current) => ({ ...current, categoryId }));
    setChartFilter(undefined);
    setPage(0);
  }, []);
  const categoryColumns = useMemo(() => {
    const helper = createDashboardColumnHelper<TicketAnalyticsCategory>();

    return [
      helper.accessor("name", {
        header: t("page.analytics-tickets.category-performance.category"),
        cell: (cell) => (
          <Button variant="subtle" size="sm" onClick={() => handleCategoryFilter(cell.row.original.id)}>
            {cell.getValue()}
          </Button>
        ),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("tickets", {
        header: t("page.analytics-tickets.category-performance.tickets"),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("openTickets", {
        header: t("page.analytics-tickets.category-performance.open"),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("medianResolutionDays", {
        header: t("page.analytics-tickets.category-performance.median-resolution"),
        cell: (cell) => {
          const medianResolutionDays = cell.getValue();

          return (
            <span>
              {medianResolutionDays === undefined
                ? "-"
                : t("page.analytics-tickets.value.days", { count: medianResolutionDays })}
            </span>
          );
        },
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("averageRating", {
        header: t("page.analytics-tickets.category-performance.satisfaction"),
        cell: (cell) => {
          const rating = cell.getValue();

          return rating === undefined ? "-" : <RatingBadge rating={rating} outOf={10} />;
        },
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
    ];
  }, [handleCategoryFilter, t]);
  const categoryRows = useMemo(
    () => [...(data?.categories ?? [])].sort((a, b) => b.tickets - a.tickets).slice(0, CATEGORY_TABLE_SIZE),
    [data?.categories],
  );
  const categoryTable = useResizableTable(TABLE_KEYS.ANALYTICS_TICKET_CATEGORIES, {
    columns: categoryColumns,
    data: categoryRows,
    initialState: {
      sorting: [{ id: "tickets", desc: true }],
    },
  });

  const activeFilterCount =
    Number(filter.categoryId !== "all") + Number(filter.status !== "all") + Number(filter.visibility !== "all");
  const handleDateRangeChange = (option: TicketAnalyticsDateRangeOption) => {
    setSelectedDateRange(option.value);
    setChartFilter(undefined);
    setPage(0);
  };
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };
  const handleFilterUpdate = (value: TicketAnalyticsFilterValues) => {
    setFilter(value);
    setChartFilter(undefined);
    setPage(0);
  };
  const handleChartFilter = (value: NonNullable<TicketAnalyticsChartFilter>) => {
    setChartFilter(value);
    setPage(0);
  };

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      <TicketAnalyticsContent
        mode={mode}
        data={data}
        isLoading={isLoading}
        isFetching={isFetching}
        selectedDateRange={selectedDateRangeOption}
        dateRangeOptions={dateRangeOptions}
        onDateRangeChange={handleDateRangeChange}
        search={search}
        onSearchChange={handleSearchChange}
        activeFilterCount={activeFilterCount}
        onOpenFilters={filterOpenHandlers.setTrue}
        ticketTable={ticketTable}
        categoryTable={categoryTable}
        chartFilter={chartFilter}
        onChartFilter={handleChartFilter}
        onClearChartFilter={() => setChartFilter(undefined)}
        page={currentPage}
        pageCount={pageCount}
        resultCount={filteredTickets.length}
        onPageChange={setPage}
      />
      <TicketAnalyticsFilterDrawer
        isOpened={isFilterOpened}
        onOpenChange={filterOpenHandlers.set}
        filter={filter}
        categories={data?.filterOptions.categories ?? []}
        onUpdate={handleFilterUpdate}
      />
    </>
  );
}
